import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Sparkles, TrendingUp, TrendingDown } from "lucide-react";
import { RevealLine, FadeUp } from "../components/Reveal";
import ArticleArt from "../components/ArticleArt";
import EnquiryForm from "../components/EnquiryForm";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  CALENDLY_URL, EMAILS, TICKER_ROWS, SERVICES, OUTCOME_TABS, ARTICLES, RESOURCES, IMAGES,
} from "../data/content";

const Eyebrow = ({ children, light = false }) => (
  <p className={`font-mono text-lg uppercase tracking-[0.25em] mb-5 ${light ? "text-mint" : "text-ink-mid"}`} data-testid="section-eyebrow">
    {children}
  </p>
);

function Hero() {
  return (
    <section className="relative pt-36 pb-20 lg:pt-44 lg:pb-28 overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0" aria-hidden="true">
        <img src="/images/hero-bg.jpeg" alt="" className="w-full h-full object-cover opacity-70" data-testid="hero-bg-image" />
        <div className="absolute inset-0 bg-gradient-to-r from-paper via-paper/75 to-paper/25" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl">
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-ink" data-testid="hero-headline">
            <RevealLine animateOnLoad delay={0.1}>Part-time CFO &amp;</RevealLine>
            <RevealLine animateOnLoad delay={0.22}>Accounting Services</RevealLine>
            <RevealLine animateOnLoad delay={0.34}>
              <span className="italic text-green">for Growing Businesses</span>
            </RevealLine>
          </h1>
          <FadeUp delay={0.45} y={20} className="mt-6">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-ink-mid" data-testid="hero-tagline">Aiding financial clarity to make better decisions</p>
          </FadeUp>
          <FadeUp delay={0.55} y={20} className="mt-6 flex items-center">
            <span className="inline-flex items-center gap-2 border border-green/30 bg-green-lt text-green font-mono text-[11px] uppercase tracking-[0.2em] px-4 py-2 rounded-full" data-testid="hero-ai-tag">
              <Sparkles size={13} /> Powered by AI
            </span>
          </FadeUp>
          <FadeUp delay={0.65} y={20} className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-primary-cta"
              className="inline-flex items-center gap-2 bg-green text-white text-sm font-medium px-7 py-4 rounded-sm hover:bg-green-mid transition-colors duration-200"
            >
              Talk to us — it's free <ArrowUpRight size={15} />
            </a>
            <a href="#services" data-testid="hero-secondary-cta" className="inline-flex items-center gap-2 border border-rule bg-paper/70 backdrop-blur-sm text-ink text-sm font-medium px-7 py-4 rounded-sm hover:bg-green-lt transition-colors duration-200">
              See what we do <ArrowRight size={15} />
            </a>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}

function ServicesIndex() {
  const items = [...TICKER_ROWS.flat(), ...TICKER_ROWS.flat()];
  return (
    <div className="py-6 border-b border-rule overflow-hidden" data-testid="services-ticker">
      <div className="ticker-track">
        {items.map((item, i) => (
          <span key={i} className="font-sans font-light text-lg text-ink-soft whitespace-nowrap">
            <span className="text-ink-mid">{item}</span>
            <span className="text-green/40 mx-6">·</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function ServicesSection() {
  return (
    <section id="services" className="py-16 lg:py-20" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Eyebrow>What we do</Eyebrow>
        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink mb-6">
          <RevealLine>Our Services.</RevealLine>
        </h2>
        <FadeUp className="max-w-xl text-ink-mid leading-relaxed mb-10">
          Four service areas. Each one built around a specific set of needs. Click any to see the full detail.
        </FadeUp>
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:gap-6">
          {SERVICES.map((s, i) => (
            <FadeUp key={s.slug} delay={i * 0.08} className="h-full">
              <Link
                to={`/services/${s.slug}`}
                data-testid={`service-card-${s.slug}`}
                className="group flex flex-col h-full bg-white rounded-2xl p-5 sm:p-7 lg:p-10 hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              >
                <span className="font-mono text-[10px] sm:text-xs text-ink-soft tracking-[0.2em] mb-4 sm:mb-6">{s.num}</span>
                <h3 className="font-serif text-lg sm:text-2xl lg:text-3xl text-ink mb-2 sm:mb-4 group-hover:text-green transition-colors duration-300">{s.title}</h3>
                <p className="text-xs sm:text-sm text-ink-mid leading-relaxed mb-4 sm:mb-8 flex-1 hidden sm:block">{s.desc}</p>
                <div className="hidden sm:flex flex-wrap gap-2 mb-8">
                  {s.pills.map((p) => (
                    <span key={p} className="text-[11px] font-mono uppercase tracking-wider bg-green-lt text-green px-3 py-1 rounded-full">{p}</span>
                  ))}
                </div>
                <span className="inline-flex items-center gap-2 text-xs sm:text-sm font-medium text-green">
                  Explore <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1.5" />
                </span>
              </Link>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function OutcomePanel({ tab }) {
  const maxBar = Math.max(...tab.bars.items.map(([, v]) => v));
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <div className="lg:col-span-8 grid grid-cols-2 gap-4">
        {tab.metrics.map((m) => (
          <div key={m.label} className="bg-paper rounded-2xl p-6 lg:p-8" data-testid={`metric-${m.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-soft mb-3">{m.label}</p>
            <p className="font-serif text-3xl lg:text-4xl text-ink">{m.value}</p>
            <p className={`mt-2 inline-flex items-center gap-1 text-xs font-medium ${m.up ? "text-green" : "text-amber"}`}>
              {m.up ? <TrendingUp size={12} /> : <TrendingDown size={12} />} {m.delta}
            </p>
          </div>
        ))}
      </div>
      <div className="lg:col-span-4 flex flex-col gap-6">
        <div className="bg-white border border-rule rounded-sm p-6 flex-1">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-soft mb-5">{tab.bars.title}</p>
          <div className="space-y-4">
            {tab.bars.items.map(([label, v]) => (
              <div key={label}>
                <div className="flex justify-between text-xs text-ink-mid mb-1.5">
                  <span>{label}</span>
                  <span className="font-mono">{typeof v === "number" && v <= 100 ? `${v}%` : v}</span>
                </div>
                <div className="h-1.5 bg-green-lt rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-green rounded-full"
                    initial={{ width: 0 }}
                    whileInView={{ width: `${(v / maxBar) * 100}%` }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-forest grain relative text-paper rounded-2xl p-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mint mb-5">{tab.extra.title}</p>
          <div className="space-y-3">
            {tab.extra.items.map(([k, v]) => (
              <div key={k} className="flex justify-between text-sm">
                <span className="text-paper/70">{k}</span>
                <span className="font-mono text-mint">{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Outcomes() {
  return (
    <section className="py-16 lg:py-20 bg-white" data-testid="outcomes-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Eyebrow>What clients see</Eyebrow>
        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink mb-10">
          <RevealLine>Your numbers, <span className="italic text-green">working for you.</span></RevealLine>
        </h2>
        <FadeUp>
          <Tabs defaultValue="d2c" data-testid="outcomes-tabs">
            <TabsList className="bg-transparent border-b border-rule rounded-none h-auto p-0 mb-10 gap-0">
              {OUTCOME_TABS.map((t) => (
                <TabsTrigger
                  key={t.id}
                  value={t.id}
                  data-testid={`outcome-tab-${t.id}`}
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-green data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-green px-6 py-3 text-sm font-medium text-ink-mid transition-colors duration-200"
                >
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {OUTCOME_TABS.map((t) => (
              <TabsContent key={t.id} value={t.id} className="mt-0">
                <OutcomePanel tab={t} />
              </TabsContent>
            ))}
          </Tabs>
          <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-soft">For representation purposes only</p>
        </FadeUp>
      </div>
    </section>
  );
}

const DIFFERENTIATORS = [
  { title: "Human team, modern tools", text: "We use the best tools available — so your work is better, faster. The technology amplifies the humans. The humans are still the story." },
  { title: "Business partner mindset", text: "We don't just close the books. We read them. Every month we flag what's changed, what needs attention, and what it means for your next quarter." },
  { title: "Structured for scale", text: "Whether you're at ₹2Cr or ₹200Cr, the process is the same — fast, consistent, and clean." },
];

function About() {
  return (
    <section id="about" className="py-16 lg:py-20" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <FadeUp>
          <div className="bg-white rounded-2xl p-8 lg:p-14 shadow-sm">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7">
                <Eyebrow>Who we are</Eyebrow>
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink mb-6">
                  <RevealLine>The Team Behind <span className="italic text-green">CFOSutra.</span></RevealLine>
                </h2>
                <div className="space-y-4 text-ink-mid leading-relaxed text-sm max-w-xl">
                  <p>CFOSutra was built on a simple belief: founders and operators deserve financial partners who speak their language, work at their pace, and care about the outcome — not just the compliance checkbox.</p>
                  <p>We work with founder-run and owner-governed businesses across industries, turning unstructured processes and fragmented data into clear financial intelligence. Cash flow forecasts that actually reflect reality. P&L that tells a story. Books that close on time, every month.</p>
                  <p>Our team uses modern accounting tools, structured workflows, and a relentless attention to detail — because good numbers come from rigorous process, not just good software. We automate the repetitive so our team spends time on analysis, not data entry.</p>
                  <p className="text-ink font-medium">If you're looking for a firm that files your returns and disappears, we're not the right fit. If you want a team that thinks like a CFO, works like a partner, and shows up every month — let's talk.</p>
                </div>
                <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" data-testid="about-cta" className="mt-7 inline-flex items-center gap-2 text-green font-medium text-sm border-b border-green/40 pb-1 hover:gap-3 transition-all duration-200">
                  Book a free 30-minute call <ArrowRight size={15} />
                </a>
              </div>
              <div className="lg:col-span-5">
                <div className="relative max-w-sm mx-auto">
                  <img src="/images/founders-portrait.png" alt="Illustrated portrait of CFOSutra founders Ranjini Karthik and Madhurya Shankar" className="w-full h-80 object-cover object-top rounded-2xl" data-testid="founders-image" />
                  <div className="absolute -bottom-5 left-5 bg-white rounded-xl px-5 py-3 shadow-md">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-soft mb-0.5">Founders</p>
                    <p className="font-serif text-lg text-ink">Ranjini Karthik · Madhurya Shankar</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-12 pt-8 border-t border-rule grid grid-cols-1 md:grid-cols-3 gap-6">
              {DIFFERENTIATORS.map((d, i) => (
                <div key={d.title} className="flex gap-4" data-testid={`differentiator-${i + 1}`}>
                  <span className="font-mono text-xs text-green pt-1 tracking-[0.2em]">0{i + 1}</span>
                  <div>
                    <h3 className="font-serif text-lg text-ink mb-1.5">{d.title}</h3>
                    <p className="text-xs text-ink-mid leading-relaxed">{d.text}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-soft">Built in Bengaluru. Serving companies worldwide. US · UK · Singapore · UAE · India</p>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

function Insights() {
  return (
    <section id="insights" className="py-16 lg:py-20 bg-green-lt/40" data-testid="insights-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Eyebrow>From Our Experience Working with Clients</Eyebrow>
        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink mb-10">
          <RevealLine>Insights.</RevealLine>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {ARTICLES.map((a, i) => (
            <FadeUp key={a.slug} delay={(i % 3) * 0.08} className="h-full">
              <Link to={`/insights/${a.slug}`} data-testid={`article-card-${a.slug}`} className="group flex flex-col h-full bg-white rounded-2xl overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                <div className="h-56 overflow-hidden">
                  <ArticleArt slug={a.slug} title={a.title} data-testid={`article-thumb-${a.slug}`} />
                </div>
                <div className="flex flex-col flex-1 p-7 pt-5">
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-green">{a.tag}</span>
                    <span className="font-mono text-[10px] text-ink-soft whitespace-nowrap">{a.readTime} · {a.author}</span>
                  </div>
                  <p className="text-sm text-ink-mid leading-relaxed flex-1">{a.excerpt}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-xs font-medium text-green uppercase tracking-wider">
                    Read <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function Resources() {
  return (
    <section id="resources" className="py-16 lg:py-20" data-testid="resources-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Eyebrow>Founder's toolkit</Eyebrow>
        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink mb-10">
          <RevealLine><span className="italic text-green">Try Our Free Tools.</span></RevealLine>
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {RESOURCES.map((r, i) => (
            <FadeUp key={r.slug} delay={i * 0.1} className="h-full">
              <Link to={r.slug} data-testid={`resource-card-${r.slug.split("/").pop()}`} className="group flex flex-col h-full bg-white rounded-2xl p-9 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                <div className="flex items-center justify-between mb-8">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-soft">{r.kind}</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-green bg-green-lt px-3 py-1 rounded-full">{r.status}</span>
                </div>
                <h3 className="font-serif text-2xl text-ink mb-4 group-hover:text-green transition-colors duration-300">{r.title}</h3>
                <p className="text-sm text-ink-mid leading-relaxed mb-8 flex-1">{r.desc}</p>
                <ul className="space-y-2 mb-8">
                  {r.features.map((f) => (
                    <li key={f} className="text-xs text-ink-mid flex items-center gap-2">
                      <span className="w-1 h-1 bg-green rounded-full" /> {f}
                    </li>
                  ))}
                </ul>
                <span className="inline-flex items-center gap-2 text-sm font-medium text-green">
                  Open tool <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1.5" />
                </span>
              </Link>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function Contact() {
  return (
    <section id="contact" className="py-16 lg:py-20 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Eyebrow>Contact</Eyebrow>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-ink mb-6">
            <RevealLine>An honest conversation</RevealLine>
            <RevealLine delay={0.1}><span className="italic text-green">about your finances.</span></RevealLine>
          </h2>
          <FadeUp className="text-ink-mid text-sm leading-relaxed mb-10 max-w-md">
            Schedule a free 30-minute call, drop us an email, or send the form — whichever suits you. We reply within one business day.
          </FadeUp>
          <FadeUp delay={0.1} className="space-y-4">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-calendly-btn" className="inline-flex items-center gap-2 bg-green text-white text-sm font-medium px-7 py-4 rounded-sm hover:bg-green-mid transition-colors duration-200">
              Book a free 30-minute call <ArrowUpRight size={15} />
            </a>
            <p className="font-mono text-xs text-ink-soft space-x-3">
              {EMAILS.map((em) => (
                <a key={em} href={`mailto:${em}`} className="text-green hover:underline" data-testid={`contact-email-${em.split("@")[0]}`}>{em}</a>
              ))}
              <span>· Bengaluru, India</span>
            </p>
          </FadeUp>
        </div>
        <div className="lg:col-span-7">
          <FadeUp delay={0.15}>
            <div className="bg-paper rounded-2xl p-8 lg:p-12">
              <EnquiryForm source="contact-section" />
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main>
      <Hero />
      <ServicesIndex />
      <ServicesSection />
      <Outcomes />
      <About />
      <Insights />
      <Resources />
      <Contact />
    </main>
  );
}
